import type { CreateProfilePayload, Profile, UpdateProfilePayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type ProfileResponse = { profile: Profile };
type ProfileMutationResponse = { profile: Profile; message: string };

/**
 * Mirrors `apps/server/src/modules/profile` (`profileController`, prefix `/profiles`). One
 * profile per user, so a single `{ type: 'Profile', id: 'SELF' }` tag covers it — no list.
 *
 * `createProfileSchema` supports an embedded `institution` sub-object, but per the current web
 * flow (`apps/web/src/profile/stores/usecases/create-profile.ts`) the institution is created via
 * a *separate* call (`createInstitution`, already covered by `institution.api.ts`'s
 * `createInstitution` mutation) after `POST /profiles` succeeds — not sent inline. This file only
 * models the `/profiles` routes themselves; orchestrating "create profile, then optionally create
 * institution" across two mutations is an app-level concern (`onQueryStarted` or a plain
 * sequential call from the UI), not something to bake into one shared endpoint.
 *
 * `recordHealthDataConsent` (`POST /profiles/health-data-consent`) is included because the route
 * is real, even though no current usecase in either app calls it standalone — both apps currently
 * pass `health_data_consent: true` inline in the `POST /profiles` body instead (`createProfile`
 * already covers that path; this mutation is for a future re-consent flow that doesn't go through
 * profile creation).
 */
export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<Profile, void>({
      query: () => ({ path: ['profiles'], method: 'get' }),
      transformResponse: (response: ProfileResponse) => response.profile,
      providesTags: [{ type: 'Profile', id: 'SELF' }],
    }),

    createProfile: builder.mutation<Profile, CreateProfilePayload>({
      query: (body) => ({ path: ['profiles'], method: 'post', body }),
      transformResponse: (response: ProfileMutationResponse) => response.profile,
      invalidatesTags: [{ type: 'Profile', id: 'SELF' }],
    }),

    updateProfile: builder.mutation<Profile, UpdateProfilePayload>({
      query: (body) => ({ path: ['profiles'], method: 'put', body }),
      transformResponse: (response: ProfileMutationResponse) => response.profile,
      invalidatesTags: [{ type: 'Profile', id: 'SELF' }],
    }),

    recordHealthDataConsent: builder.mutation<void, void>({
      query: () => ({ path: ['profiles', 'health-data-consent'], method: 'post' }),
      transformResponse: (): void => undefined,
    }),
  }),
});

export const {
  useGetProfileQuery,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useRecordHealthDataConsentMutation,
} = profileApi;
